<template>
  <li
    v-tooltip="tooltip"
    class="select__item"
    :class="{
      active: view._.selected,
      'select__item--loading': view._.loading,
      'select__item--no-options': readOnly,
    }"
  >
    <a
      ref="link"
      class="select__item-link"
      :role="tab ? 'tab' : undefined"
      :tabindex="tab ? (view._.selected ? 0 : -1) : 0"
      :aria-selected="tab ? (view._.selected ? 'true' : 'false') : undefined"
      :aria-current="!tab && view._.selected ? 'true' : undefined"
      @click="select(view)"
      @keydown.enter="select(view)"
      @keydown.space.prevent="select(view)"
      @keydown.f2.prevent="enableRename"
      @keydown.shift.f10.prevent="toggleContext"
    >
      <div class="select__item-name">
        <!-- Seam for per-view custom icons. `iconClass`/`colorClass` come from
             the view *type* today; once views carry their own `icon` and
             `color` fields this computed is the single place that has to start
             preferring them, and no template changes. -->
        <i
          class="select__item-icon"
          :class="`${iconColorClass} ${iconClass}`"
          aria-hidden="true"
        ></i>
        <span class="select__item-name-text"
          ><EditableViewName ref="rename" :view="view"></EditableViewName
        ></span>
        <div
          v-if="deactivated || viewOwnershipDeactivated"
          class="deactivated-label"
        >
          <i class="iconoir-lock"></i>
        </div>
      </div>
    </a>
    <!-- The checkmark tells a *closed* dropdown's user which row they are on.
         A permanently visible list shows selection with the row tint instead,
         so the tab variant never renders it rather than rendering and hiding
         it in CSS. -->
    <i
      v-if="view._.selected && !tab"
      class="select__item-active-icon iconoir-check"
    ></i>
    <!-- Handle-only dragging: the row body's job is selection, so only this
         grip starts a reorder. Hidden with opacity (never `display: none`) so
         it stays in the accessibility tree and can be tabbed to.
         `iconoir-drag` draws four corner-arrows converging on the centre, not
         a grip, so this uses the custom `baserow-icon-drag-handle` glyph
         instead (a real six-dot grip, `assets/icons/drag-handle.svg`). -->
    <span
      v-if="dragHandle"
      class="select__item-handle"
      data-sortable-handle
      role="button"
      tabindex="-1"
      :aria-label="$t('viewsContextItem.reorderView')"
      @click.stop
    >
      <i
        class="baserow-icon-drag-handle select__item-handle-icon"
        aria-hidden="true"
      ></i>
    </span>
    <component
      :is="deactivatedClickModal[0]"
      v-if="deactivatedClickModal !== null"
      ref="deactivatedClickModal"
      v-bind="deactivatedClickModal[1]"
      :name="viewType.getName()"
      :workspace="database.workspace"
    ></component>
    <template v-if="!readOnly && showViewContext">
      <button
        ref="contextLink"
        type="button"
        class="select__item-options"
        :tabindex="tab ? -1 : 0"
        :aria-label="$t('viewsContextItem.viewOptions')"
        aria-haspopup="menu"
        @click="toggleContext"
        @mousedown.stop
      >
        <i class="baserow-icon-more-vertical" aria-hidden="true"></i>
      </button>
      <ViewContext
        ref="context"
        :database="database"
        :table="table"
        :view="view"
        :store-prefix="storePrefix"
        @enable-rename="enableRename"
      ></ViewContext>
    </template>
  </li>
</template>

<script>
import context from '@baserow/modules/core/mixins/context'
import ViewContext from '@baserow/modules/database/components/view/ViewContext'
import EditableViewName from '@baserow/modules/database/components/view/EditableViewName'
import {
  getViewIconClass,
  getViewColorClass,
  viewHasCustomIcon,
} from '@baserow/modules/database/utils/viewIdentity'

export default {
  name: 'ViewsContextItem',
  components: { EditableViewName, ViewContext },
  mixins: [context],
  props: {
    database: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: true,
    },
    storePrefix: {
      type: String,
      required: true,
    },
    /**
     * Renders the row as a `tab` in a `tablist` with a roving tabindex, rather
     * than as a plain link. Defaults to `false` so ViewsContext -- the dropdown
     * public views still use -- keeps the `aria-current` link semantics and the
     * independently focusable rows it has today.
     */
    tab: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Renders a handle-only drag grip. Defaults to `false`: the dropdown makes
     * the whole row draggable today and must not change.
     */
    dragHandle: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Falls back to the view's name as the tooltip when the row is too narrow
     * to show it, which is the collapsed rail's case. Defaults to `false` so
     * the dropdown keeps showing only the deactivated reason, as today.
     * Lives here rather than as a `v-tooltip` on the caller's side because the
     * tooltip directive keeps its state on the element: a second binding on the
     * same root node would clobber the deactivated tooltip below.
     */
    nameTooltip: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['selected'],
  computed: {
    /**
     * One binding for the row's single tooltip. The deactivated reason wins,
     * because it explains why the row will not do what it looks like it does;
     * the name is only a fallback for when the row cannot render it.
     */
    tooltip() {
      if (this.deactivated) {
        return this.deactivatedText
      }
      if (this.nameTooltip) {
        // A custom icon replaces the type icon, so on the collapsed rail --
        // where there is no label either -- the tooltip becomes the only place
        // the view type is still readable. Appending it there keeps "is this a
        // grid or a gallery?" answerable without a permanent type badge.
        return viewHasCustomIcon(this.view)
          ? `${this.view.name} · ${this.viewType.getName()}`
          : this.view.name
      }
      return null
    },
    viewType() {
      return this.$registry.get('view', this.view.type)
    },
    /**
     * The view's own icon and colour when it has them, falling back to the view
     * type's. Both resolve through `utils/viewIdentity` so the fallback rule
     * has exactly one definition across the rail, the collapsed rail and every
     * other surface that shows a view.
     */
    iconClass() {
      return getViewIconClass(this.view)
    },
    iconColorClass() {
      return getViewColorClass(this.view)
    },
    viewOwnershipType() {
      return this.$registry.get('viewOwnershipType', this.view.ownership_type)
    },
    deactivated() {
      return (
        !this.readOnly &&
        this.viewType.isDeactivated(this.database.workspace.id)
      )
    },
    viewOwnershipDeactivated() {
      return this.viewOwnershipType.isDeactivated(this.database.workspace.id)
    },
    deactivatedText() {
      if (this.deactivated) {
        return this.viewType.getDeactivatedText({ view: this.view })
      }
      if (this.viewOwnershipDeactivated) {
        return this.viewOwnershipType.getDeactivatedText()
      }
      return null
    },
    deactivatedClickModal() {
      if (this.deactivated) {
        return this.viewType.getDeactivatedClickModal()
      }
      if (this.viewOwnershipDeactivated) {
        return this.viewOwnershipType.getDeactivatedModal()
      }
      return null
    },
    showViewContext() {
      return (
        this.$hasPermission(
          'database.table.run_export',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.import_rows',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.view.duplicate',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.create_webhook',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.view.update',
          this.view,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.view.delete',
          this.view,
          this.database.workspace.id
        )
      )
    },
  },
  methods: {
    enableRename() {
      this.$refs.rename.edit()
    },
    /**
     * Also reachable with Shift+F10 from the row, which is why it tolerates the
     * menu not existing -- a read-only row, or one the user lacks every
     * permission for, renders no trigger at all.
     */
    toggleContext() {
      if (!this.$refs.context) {
        return
      }
      this.$refs.context.toggle(this.$refs.contextLink, 'bottom', 'right', 0)
    },
    /**
     * Called by the parent when a roving tabindex moves onto this row. Moving
     * focus is all this does -- it deliberately does not select, because
     * selecting a view triggers a fetch.
     */
    focus() {
      this.$refs.link?.focus()
    },
    select(view) {
      if (this.deactivated || this.viewOwnershipDeactivated) {
        this.$refs.deactivatedClickModal.show()
        return
      }
      this.$emit('selected', view)
    },
  },
}
</script>
